/**
 * Real tasks on an instance (DEC-0452). A `task` instance belongs to a host
 * instance through a note_relations row, relation_type 'task_of'
 * (source = the task, target = the host). Same table as blocked_by / subtask_of.
 * Subtasks are shown under their parent task, not here, so they are left out.
 */
import { supabase } from '@/lib/supabaseClient';
import { createNote, createNoteRelation, deleteNote, deleteNoteRelation } from '@/lib/supabase/entityQueries';
import { SUBTASK_OF_RELATION } from '@/lib/entities/taskSubtasks';
import { TASK_ENTITY_TYPE } from '@/lib/supabase/taskLibraryQueries';

export const TASK_OF_RELATION = 'task_of';

export interface InstanceTaskLink {
  id: string;
  title: string;
  status: string | null;
  /** relation row id, so the link can be removed. */
  relationId: string;
}

export async function fetchInstanceTaskLinks(hostId: string): Promise<InstanceTaskLink[]> {
  const { data: rels, error } = await supabase
    .from('note_relations')
    .select('id, source_id')
    .eq('relation_type', TASK_OF_RELATION)
    .eq('target_id', hostId);
  if (error) { console.error('instanceTaskLinks relations:', error.message); return []; }
  const ids = (rels ?? []).map((r) => r.source_id as string);
  if (ids.length === 0) return [];

  const [{ data: records, error: recErr }, { data: subs }] = await Promise.all([
    supabase
      .from('vb_records')
      .select('id, title, meta, created_at')
      .in('id', ids)
      .eq('is_deleted', false)
      .order('created_at', { ascending: true }),
    supabase.from('note_relations').select('source_id').eq('relation_type', SUBTASK_OF_RELATION).in('source_id', ids),
  ]);
  if (recErr) { console.error('instanceTaskLinks records:', recErr.message); return []; }
  const subtaskIds = new Set((subs ?? []).map((s) => s.source_id as string));

  return (records ?? [])
    .filter((r) => !subtaskIds.has(r.id as string))
    .map((r) => {
      const status = (r.meta as Record<string, unknown> | null)?.status;
      return {
        id: r.id as string,
        title: (r.title as string) ?? '',
        status: typeof status === 'string' ? status : null,
        relationId: (rels ?? []).find((rel) => rel.source_id === r.id)!.id as string,
      };
    });
}

export type AddInstanceTaskResult = { ok: true; id: string } | { ok: false; error: string };

/** Creates a new task instance and links it to the host instance. */
export async function addInstanceTask(hostId: string, title: string): Promise<AddInstanceTaskResult> {
  const name = title.trim();
  if (!name) return { ok: false, error: 'חסר שם למשימה' };
  const note = await createNote(name, TASK_ENTITY_TYPE, { status: 'todo' });
  if (!note) return { ok: false, error: 'יצירת המשימה נכשלה' };
  const link = await createNoteRelation(note.id, hostId, TASK_OF_RELATION);
  if (!link) {
    await deleteNote(note.id); // no orphan task left behind, so a retry cannot duplicate it
    return { ok: false, error: 'יצירת המשימה נכשלה (הקישור למופע)' };
  }
  return { ok: true, id: note.id };
}

/** Removes only the link; the task stays as an ordinary task in the library. */
export async function unlinkInstanceTask(relationId: string): Promise<boolean> {
  return deleteNoteRelation(relationId);
}
